import { Grid, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { EmeraldErrorModal } from 'src/common/api/emerald-error';
import BaseButton from 'src/common/components/base-button';
import { codeContainerStyles } from 'src/common/components/code-container/components/code-container-styles';
import ProductCodeContainer from 'src/common/components/code-container/components/product-code-container';
import { setHomePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { PaymentException } from 'src/common/exceptions/paymentException';
import { IButton } from 'src/common/interfaces/IButton';
import barcode from 'src/common/media/barcode.svg';
import keypad from 'src/common/media/keypad.svg';
import transaction from 'src/common/media/Transactions.svg';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { PmiStatusCodes } from 'src/devices/payment/pmi-status-codes-enums';
import { ScannedDataHandler } from 'src/devices/scanner-operations';
import Keypad from 'src/keypad/components/keypad';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { OnlineItemExternalId } from 'src/online-items/enumerations/OnlineItemAuthorizationTrigger';
import { checkForCardDetails } from 'src/tendering/business-logic/check-for-card-details';
import TenderAmountDue from 'src/tendering/components/tender-amount-due';

type Props = IGiftCardRefundProps;

export const showGiftCardRefundContainer = (
  props: IGiftCardRefundProps,
  onScan: ScannedDataHandler,
): void => {
  setHomePage(<GiftCardRefundContainer {...props} />, onScan);
};

const GiftCardRefundContainer: React.FC<Props> = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const balanceDue = useSelector((state: AppState) => state.itemList.amountDue);
  const hasCardData = useSelector((state: AppState) => state.pinPad.hasCardData);
  const authorizationExternalIdType = useSelector(
    (state: AppState) => state.balanceInquiry.authorizationExternalIdType,
  );
  const { onSwipe, onKeypadSubmit, onCancel } = props;

  const [getCardDetailsError, setGetCardDetailsError] = useState<PaymentException | undefined>();

  const keypadSubmitButton: IButton = {
    label: 'OK',
    onClick: onKeypadSubmit,
  };

  const prepareGiftCardData = useCallback((): void => {
    checkForCardDetails(1).catch(setGetCardDetailsError);
  }, []);

  useEffect(() => {
    if (getCardDetailsError) {
      if (getCardDetailsError.statusCode === PmiStatusCodes.Cancelled) {
        popupMessage(
          new EmeraldErrorModal({
            type: EmeraldDialogTypes.Info,
            message: 'RefundDeclined',
            subMessage: 'EPS_RefundCancelledByCustomerMessage',
          }),
        );
      } else {
        popupMessage(
          new EmeraldErrorModal({
            type: EmeraldDialogTypes.Info,
            message: 'GetGiftCardDetaildFailed',
            subMessage: 'GetGiftCardDetaildFailedSubmessage',
          }),
        );
      }
      setGetCardDetailsError(undefined);
      prepareGiftCardData();
    }
  }, [getCardDetailsError, prepareGiftCardData]);

  useEffect(() => {
    if (!authorizationExternalIdType || authorizationExternalIdType === OnlineItemExternalId.Card) {
      if (hasCardData) {
        onSwipe();
      } else {
        prepareGiftCardData();
      }
    }
  }, [hasCardData, authorizationExternalIdType, prepareGiftCardData, onSwipe]);

  return (
    <ProductCodeContainer
      headerTitle={t('GiftCardRefund')}
      titleSubContainerComponent={<TenderAmountDue amountDue={balanceDue} align='right' />}
      onKeypadSubmit={onKeypadSubmit}
      keypadComponent={
        <Keypad
          keyboardId='gift-card-refund'
          disableManualWeight
          disableQuantity
          topLabelText=' '
          submitButton={keypadSubmitButton}
          keypadTestIdPrefix='gift-card-refund-'
          visible
        />
      }
    >
      <GiftCardRefundScanWindow
        onCancel={onCancel}
        authorizationExternalIdType={authorizationExternalIdType}
      />
    </ProductCodeContainer>
  );
};

const GiftCardRefundScanWindow = (props: BalanceInquiryScanWindowProps): JSX.Element => {
  const classes = codeContainerStyles();
  const { t } = useTranslation();
  const trainingModeClasses = trainingModeStyles();
  const trainingMode = useSelector((state: AppState): boolean => state.posMode.trainingMode);

  return (
    <Grid
      item
      className={`fullHeight flexFull ${classes.outerMostContainer}`}
      data-testid='gift-card-refund'
    >
      <Grid
        item
        className={`${
          trainingMode ? trainingModeClasses.trainingModeBackground : classes.background
        } fullWidth fullHeight`}
      >
        <Grid
          container
          direction='column'
          className={`fullHeight ${classes.outerFlexDesign}`}
          data-testid='gift-card-refund-scanning-container'
        >
          <Grid
            item
            container
            className={classes.flexDesign}
            data-testid='gift-card-refund-body-container'
          >
            {!props.authorizationExternalIdType && <DefaultInstructions />}
            {props.authorizationExternalIdType === OnlineItemExternalId.Card && (
              <CardSwipeInstructions />
            )}
            {props.authorizationExternalIdType === OnlineItemExternalId.SecondBarcode && (
              <SecondBarcodeInstructions />
            )}
            <Grid item container justifyContent='center' className={classes.selfAlign}>
              <BaseButton
                buttonType='white'
                onClick={props.onCancel}
                data-testid='gift-card-refund-cancel-button'
              >
                <Typography variant={'button'}>{t('Cancel')}</Typography>
              </BaseButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const DefaultInstructions = () => {
  const classes = codeContainerStyles();
  const { t } = useTranslation();
  return (
    <Grid item container className={classes.containerAlignment}>
      <Grid className={classes.spacing}>
        <Typography variant={'h4'} data-testid='gift-card-refund-body-text1'>
          {t('GiftCardInsertSwipeOrTap')}
        </Typography>
      </Grid>
      <Grid
        container
        justifyContent='center'
        className={classes.spacing}
        data-testid='gift-card-refund-icon-1'
      >
        <img src={transaction} alt='transaction' className={classes.icon} />
      </Grid>
      <Grid container justifyContent='center' className={classes.spacing}>
        <Typography variant={'h4'} data-testid='gift-card-refund-body-text2'>
          {t('GiftCardOR')}
        </Typography>
      </Grid>
      <Grid container justifyContent='center' className={classes.spacing}>
        <Typography variant={'h4'} data-testid='gift-card-refund-body-text3'>
          {t('GiftCardScanKey')}
        </Typography>
      </Grid>
      <Grid
        container
        justifyContent='center'
        className={classes.spacing}
        data-testid='barcode-pinpad-icon-container'
      >
        <Grid className={classes.iconPadding}>
          <img src={barcode} alt='Barcode' className={classes.icon} />
        </Grid>
        <img src={keypad} alt='keypad' className={classes.icon} />
      </Grid>
    </Grid>
  );
};

const CardSwipeInstructions = () => {
  const classes = codeContainerStyles();
  const { t } = useTranslation();
  return (
    <Grid item container className={classes.containerAlignment}>
      <Grid className={classes.spacing}>
        <Typography variant={'h4'} data-testid='gift-card-refund-body-text1'>
          {t('GiftCardPleaseSwipeCard')}
        </Typography>
      </Grid>
      <Grid
        container
        justifyContent='center'
        className={classes.spacing}
        data-testid='gift-card-refund-icon-1'
      >
        <img src={transaction} alt='transaction' className={classes.icon} />
      </Grid>
    </Grid>
  );
};

const SecondBarcodeInstructions = () => {
  const classes = codeContainerStyles();
  const { t } = useTranslation();
  return (
    <Grid item container className={classes.containerAlignment}>
      <Grid className={classes.spacing}>
        <Typography variant={'h4'} data-testid='gift-card-refund-body-text1'>
          {t('GiftCardScanSecond')}
        </Typography>
      </Grid>
      <Grid
        container
        justifyContent='center'
        className={classes.spacing}
        data-testid='barcode-pinpad-icon-container'
      >
        <Grid className={classes.iconPadding}>
          <img src={barcode} alt='Barcode' className={classes.icon} />
        </Grid>
        <img src={keypad} alt='keypad' className={classes.icon} />
      </Grid>
    </Grid>
  );
};

export interface BalanceInquiryScanWindowProps {
  onCancel: () => void;
  authorizationExternalIdType?: OnlineItemExternalId;
}

export interface IGiftCardRefundProps {
  onCancel: () => void;
  onKeypadSubmit: (itemId: string) => void;
  onSwipe: () => void;
}

export default GiftCardRefundContainer;
